import Cookies from 'js-cookie';
import { languageUtils } from '@/utils/languageUtils';
import { apiFetch } from './apiService';
import { getAccessToken } from '@/utils/authStorage';
import { CHALO_API_BASE, AUTH_API_BASE } from '@/config/apiConfig';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  success: boolean;
  message?: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export type { LoginCredentials, RegisterData, AuthResponse, User };

// --- OTP Auth APIs ---

export async function registerDeviceId({ phoneNumber }: { phoneNumber: string }) {
  const deviceId = phoneNumber;
  const res = await fetch(`${AUTH_API_BASE}/api/auth/device-id`, {
    method: 'POST',
    headers: {
      'accept': '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deviceId }),
  });
  return res;
}

export async function requestOtp({ phoneNumber, countryCode }: { phoneNumber: string; countryCode: string }) {
  // deviceId is same as phoneNumber
  const deviceId = phoneNumber;
  // Get language from cookie, fallback to current language from languageUtils
  const lang = Cookies.get('chalo_language') || languageUtils.getCurrentLanguage();
  // Register deviceId first
  const regRes = await registerDeviceId({ phoneNumber });
  if (!regRes.ok) {
    throw new Error('Failed to register device ID');
  }
  const res = await fetch(`${AUTH_API_BASE}/api/auth/request-otp`, {
    method: 'POST',
    headers: {
      'accept': '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deviceId, phoneNumber, countryCode, lang }),
  });
  return res;
}

export async function getOtp({ phoneNumber }: { phoneNumber: string }) {
  // deviceId is same as phoneNumber
  const deviceId = phoneNumber;
  const url = `${AUTH_API_BASE}/api/test/show-otp?deviceid=${encodeURIComponent(deviceId)}&phone=${encodeURIComponent(phoneNumber)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'accept': 'text/plain' },
  });
  return res;
}

// --- Anonymous (booking) web-user registration ---

export interface CreateWebUserPayload {
  phoneNumber: string;
  countryCode: string;
  firstName: string;
  lastName: string;
  gender: string;
  /** Access token returned by validate-otp; sent as a Bearer token to authorize the call. */
  accessToken: string;
}

export interface CreateWebUserResult {
  accessToken: string;
  refreshToken: string;
}

/**
 * Registers a lightweight anonymous web user (used by the public Book Now flow) and
 * returns freshly minted auth tokens. Unlike the full app registration this endpoint
 * only needs phone, country code, name and gender, and is authorized with the access
 * token issued during OTP verification.
 */
export async function createWebUser({
  phoneNumber,
  countryCode,
  firstName,
  lastName,
  gender,
  accessToken,
}: CreateWebUserPayload): Promise<CreateWebUserResult> {
  const res = await fetch(`${AUTH_API_BASE}/api/AnonymousUser/create-web-user`, {
    method: 'POST',
    headers: {
      accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      ph: phoneNumber,
      cc: countryCode,
      fn: firstName,
      ln: lastName,
      g: gender,
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to create web user (status ${res.status})`);
  }

  const data = await res.json();
  const newAccessToken = data?.accessToken ?? data?.token;
  const newRefreshToken = data?.refreshToken;

  if (!newAccessToken || !newRefreshToken) {
    throw new Error('Invalid response: tokens missing');
  }

  return { accessToken: newAccessToken, refreshToken: newRefreshToken };
}

// --- Cookie Helpers ---
// Re-export from apiService for backward compatibility
export { setAuthCookies, getAccessToken, getDeviceId, clearAuthCookies, clearAuthSession } from '@/utils/authStorage';

export function getRefreshToken() {
  return Cookies.get('refreshToken') || null;
}

// --- Update validateOtp to save tokens if present ---
export async function validateOtp({ 
  phoneNumber, 
  otp, 
  countryCode, 
  latitude, 
  longitude 
}: { 
  phoneNumber: string; 
  otp: string; 
  countryCode?: string; 
  latitude?: number | null; 
  longitude?: number | null; 
}) {
  const deviceId = phoneNumber;
  
  
  const formattedCountryCode = countryCode || '';
  
  // Default latitude and longitude to 0 if not provided
  const lat = latitude !== null && latitude !== undefined && Number.isFinite(latitude) ? latitude : 0;
  const lng = longitude !== null && longitude !== undefined && Number.isFinite(longitude) ? longitude : 0;
  
  const res = await fetch(`${AUTH_API_BASE}/api/auth/validate-otp`, {
    method: 'POST',
    headers: {
      'accept': '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      deviceId, 
      phoneNumber, 
      otp, 
      countryCode: formattedCountryCode,
      latitude: lat,
      longitude: lng
    }),
  });
  if (res.ok) {
    const data = await res.json();
    // Do NOT set cookies here. Just return the tokens and deviceId for the caller to handle.
    return { ok: true, data };
  }
  return { ok: false, error: await res.text() };
}

// --- Input Validation Helpers ---

export function validatePhoneNumber(phone: string, countryCode: string = '+91'): string | null {
  const cleaned = phone.replace(/\D/g, '');
  if (!cleaned) return 'Phone number is required';
  if (countryCode === '+91') {
    if (cleaned.length !== 10) return 'Phone number must be exactly 10 digits';
    if (!/^[6-9]\d{9}$/.test(cleaned)) return 'Enter a valid Indian phone number (starts with 6-9)';
  } else {
    if (cleaned.length < 7 || cleaned.length > 15) return 'Enter a valid phone number';
  }
  return null;
}

export function validateOtpInput(otp: string): string | null {
  if (!otp) return 'OTP is required';
  if (!/^\d{6}$/.test(otp)) return 'OTP must be 6 digits';
  return null;
}

export async function getHelpInfo() {
  const lang = languageUtils.getCurrentLanguage();
    const res = await fetch(`${CHALO_API_BASE}/support`, {
    method: 'GET',
    headers: { 'accept': '*/*', 'lang': lang },
  });
  if (!res.ok) throw new Error('Failed to fetch help info');
  return res.json();
}

// This function is now replaced by the centralized apiFetch in apiService.ts
// Keeping for backward compatibility but it's deprecated
export async function refreshTokenIfNeeded(fetchFn: () => Promise<Response>): Promise<Response> {
  console.warn('refreshTokenIfNeeded is deprecated. Use apiFetch from apiService instead.');
  return fetchFn();
}

export async function getMyIssues() {
  const accessToken = getAccessToken();
  if (!accessToken) return { data: null, status: 401, message: 'Unauthorized' };
  const lang = languageUtils.getCurrentLanguage();
  
  const res = await apiFetch(`${CHALO_API_BASE}/my-issues`, {
    method: 'GET',
    headers: {
      'accept': 'application/json',
      'lang': lang,
    },
  });
  
  let data = null;
  let message = '';
  try {
    data = await res.json();
    message = data?.message || '';
  } catch (e) {
    message = 'An error occurred.';
  }
  return { data, status: res.status, message };
}

export async function getReportIssueForm() {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authorized');
  const lang = languageUtils.getCurrentLanguage();
  
  const res = await apiFetch(`${CHALO_API_BASE}/report-issue`, {
    method: 'GET',
    headers: {
      'accept': 'text/plain',
      'lang': lang,
    },
  });
  
  if (!res.ok) throw new Error('Failed to fetch report issue form');
  return res.json();
}

/**
 * Submit an issue using the new API format.
 * @param body { ans: Array<{ qid: string, qcode: string, ans: string }> }
 * @returns API response object (parsed JSON or text)
 */
export async function submitIssue(body: { ans: { qid: number; qcode: string; ans: string }[] }) {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authorized');
  
  const res = await apiFetch(`${CHALO_API_BASE}/submit-reportissue`, {
    method: 'PUT',
    headers: {
      'accept': 'text/plain',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  
  const text = await res.text();
  let data: any = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }
  }
  return { ok: res.ok, ...data };
}

export async function saveUserProfile({ name, language, role }: { name: string; language: string; role: string }) {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authorized');
  
  const body = {
    nm: name,
    lang: language,
    role,
  };

  console.log('[AuthService] Storing user profile...');
  console.log('[AuthService] POST /usersstore-profile request:', {
    url: `${CHALO_API_BASE}/usersstore-profile`,
    body,
  });

  const res = await apiFetch(`${CHALO_API_BASE}/usersstore-profile`, {
    method: 'POST',
    headers: {
      'accept': '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await res.json();
  console.log('[AuthService] POST /usersstore-profile response:', {
    status: res.status,
    ok: res.ok,
    data,
  });

  return data;
}

export async function getUserProfile() {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authorized');
  
  const res = await apiFetch(`${CHALO_API_BASE}/get-userprofile?shorttype=false`, {
    method: 'GET',
    headers: {
      'accept': '*/*',
    },
  });
  
  if (!res.ok) throw new Error('Failed to fetch user profile');
  return res.json();
}
export async function updateUserProfile(profile: {
  fn: string;
  ln: string;
  em: string;
  //ph: string;
  dob: string;
  g: string;
  adr: string;
  ut: string;
  ct: string;
  cty: string;
  cc: string;
  st: string;
  pin: string;
  ec: string;
  ecc: string;
  er: string;
}) {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('Not authorized');
  const lang = languageUtils.getCurrentLanguage();
  
  const res = await apiFetch(`${CHALO_API_BASE}/update-userprofile`, {
    method: 'PUT',
    headers: {
      'accept': '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...profile, lang }),
  });
  
  if (!res.ok) throw new Error('Failed to save user profile');
  return res.json();
}